/**
 * FinderListView Component
 *
 * List view with optional preview pane.
 */

import { useCallback } from 'react'
import { ContentItem } from './FinderWindow'
import { FinderPreview } from './FinderPreview'
import './finder.css'

interface FinderListViewProps {
  items: ContentItem[]
  selectedItem: ContentItem | null
  onItemSelect: (item: ContentItem) => void
  onItemDoubleClick?: (item: ContentItem) => void
  showPreview?: boolean
}

// Get display name for item
const getDisplayName = (item: ContentItem): string => {
  return item.name || item.company || item.title || item.slug
}

// Get date info for item
const getDateInfo = (item: ContentItem): string | null => {
  if (item.start && item.end) {
    return `${item.start} - ${item.end}`
  }
  if (item.start) {
    return item.start as string
  }
  if (item.years) {
    return `${item.years} years`
  }
  return null
}

// Get kind/type info
const getKindInfo = (item: ContentItem): string | null => {
  if (item.type) {
    const typeLabels: Record<string, string> = {
      employment: 'Employment',
      founded: 'Founded',
      'co-founded': 'Co-founded',
      contract: 'Contract',
    }
    return typeLabels[item.type as string] || (item.type as string)
  }
  if (item.proficiency) {
    return item.proficiency as string
  }
  return null
}

export function FinderListView({
  items,
  selectedItem,
  onItemSelect,
  onItemDoubleClick,
  showPreview = true,
}: FinderListViewProps) {
  const handleClick = useCallback(
    (item: ContentItem, e: React.MouseEvent) => {
      e.preventDefault()
      onItemSelect(item)
    },
    [onItemSelect]
  )

  const handleDoubleClick = useCallback(
    (item: ContentItem, e: React.MouseEvent) => {
      e.preventDefault()
      onItemDoubleClick?.(item)
    },
    [onItemDoubleClick]
  )

  const handleKeyDown = useCallback(
    (item: ContentItem, e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onItemSelect(item)
      }
    },
    [onItemSelect]
  )

  if (items.length === 0) {
    return (
      <div className="finder-list-view finder-list-view--empty">
        <p>No items in this category</p>
      </div>
    )
  }

  return (
    <div className={`finder-list-view ${showPreview ? 'with-preview' : ''}`}>
      <div className="finder-list-container">
        {/* Header row */}
        <div className="finder-list-header">
          <div className="finder-list-header__name">Name</div>
          <div className="finder-list-header__date">Date</div>
          <div className="finder-list-header__kind">Kind</div>
        </div>

        {/* List items */}
        <ul className="finder-list" role="listbox">
          {items.map((item) => {
            const isSelected = selectedItem?.slug === item.slug
            const displayName = getDisplayName(item)
            const dateInfo = getDateInfo(item)
            const kindInfo = getKindInfo(item)

            return (
              <li key={item.slug}>
                <button
                  className={`finder-list-item ${isSelected ? 'selected' : ''}`}
                  onClick={(e) => handleClick(item, e)}
                  onDoubleClick={(e) => handleDoubleClick(item, e)}
                  onKeyDown={(e) => handleKeyDown(item, e)}
                  aria-selected={isSelected}
                  role="option"
                >
                  <div className="finder-list-item__name">{displayName}</div>
                  <div className="finder-list-item__date">{dateInfo || '—'}</div>
                  <div className="finder-list-item__kind">{kindInfo || '—'}</div>
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      {/* Preview pane */}
      {showPreview && (
        <div className="finder-list-preview">
          <FinderPreview item={selectedItem} />
        </div>
      )}
    </div>
  )
}

export default FinderListView
